import * as React from "react";
import Modal from "@mui/joy/Modal";
import Box from "@mui/joy/Box";
import IconButton from "@mui/joy/IconButton";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { CrossBlackIcon, RightArrowBlackIcon } from "@/src/Asset";

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedImage: string | null;
  images: any;
  setSelectedImage: (url: string | null) => void;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  selectedImage,
  images,
  setSelectedImage,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState<number>(0);
  const [direction, setDirection] = React.useState<number>(0);
  const thumbnailContainerRef = React.useRef<HTMLDivElement>(null);

  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
    }),
  };

  React.useEffect(() => {
    if (selectedImage) {
      const index = images?.findIndex((image: any) => {
        const url = image?.url || image?.video_url;
        return url === selectedImage;
      });
      setCurrentIndex(index);
    }
  }, [selectedImage, images]);

  React.useEffect(() => {
    if (thumbnailContainerRef.current) {
      const containerWidth = thumbnailContainerRef.current.clientWidth;
      const thumbnailWidth = 100; // Thumbnail width
      const marginRight = 8; // Space between thumbnails
      const scrollTo =
        currentIndex * (thumbnailWidth + marginRight) -
        containerWidth / 2 +
        thumbnailWidth / 2;
      thumbnailContainerRef.current.scrollTo({
        left: scrollTo,
        behavior: "smooth",
      });
    }
  }, [currentIndex]);

  const handleNext = () => {
    setDirection(1);
    const nextIndex =
      currentIndex === images?.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(nextIndex);
    setSelectedImage(images[nextIndex]?.url || images[nextIndex]?.video_url);
  };

  const handlePrevious = () => {
    setDirection(-1);
    const prevIndex =
      currentIndex === 0 ? images?.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedImage(images[prevIndex]?.url || images[prevIndex]?.video_url);
  };

  const isVideo = (url: string) => {
    return url?.includes("youtube.com") || url?.includes("vimeo.com");
  };

  const getVideoThumbnailUrl = (url: string) => {
    if (url?.includes("youtube.com")) {
      const videoId = url?.split("v=")[1]?.split("&")[0];
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`; // YouTube thumbnail
    } else if (url?.includes("vimeo.com")) {
      const videoId = url?.split("vimeo.com/")[1];
      return `https://vumbnail.com/${videoId}.jpg`; // Vimeo thumbnail (using vumbnail.com)
    }
    return null;
  };

  const getVideoEmbedUrl = (url: string) => {
    if (url?.includes("youtube.com")) {
      const videoId = url?.split("v=")[1]?.split("&")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url?.includes("vimeo.com")) {
      const videoId = url?.split("vimeo.com/")[1];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return null;
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="image-modal-title"
      aria-describedby="image-modal-description"
    >
      <Box className="fixed inset-0 flex items-center justify-center bg-slate-900 bg-opacity-75">
        <Box className="relative w-full max-w-screen-md mx-auto px-4 md:px-6">
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 20,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
            }}
          >
            <CrossBlackIcon />
          </IconButton>

          {/* Main Image or Video */}
          <Box className="flex justify-center items-center h-48 sm:h-72 md:h-[450px] px-4 relative">
            <AnimatePresence initial={false} custom={direction}>
              {selectedImage && (
                <motion.div
                  key={selectedImage}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 },
                  }}
                  className="absolute"
                >
                  {isVideo(selectedImage) ? (
                    <iframe
                      src={getVideoEmbedUrl(selectedImage) || ""}
                      className="object-cover w-[280px] h-[160px] sm:w-[400px] sm:h-[250px] md:w-[650px] md:h-[400px]"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <Image
                      src={selectedImage}
                      alt="Selected"
                      height={1000}
                      width={1000}
                      className="object-cover border border-white w-[280px] h-[160px] sm:w-[400px] sm:h-[250px] md:w-[650px] md:h-[400px]"
                      style={{
                        objectFit: "cover",
                      }}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          {/* Thumbnail List */}
          <Box
            ref={thumbnailContainerRef}
            className="flex gap-1 mt-4 md:mt-5 overflow-x-auto w-full"
            sx={{
              height: 80,
              whiteSpace: "nowrap",
              alignItems: "center",
              scrollbarWidth: "hide",
              "&::-webkit-scrollbar": {
                height: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                borderRadius: 4,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
            }}
          >
            {images?.map((image: any, index: number) => {
              const thumbnailUrl = isVideo(image?.video_url)
                ? getVideoThumbnailUrl(image?.video_url)
                : image?.url;
              return (
                <Box
                  key={index}
                  className="relative mr-3 flex w-full gap-x-4"
                  sx={{
                    display: "inline-block",
                    width: 100,
                    height: 60,
                    cursor: "pointer",
                    border: currentIndex === index ? "2px solid white" : "none",
                    opacity: currentIndex === index ? 1 : 0.7,
                  }}
                  onClick={() => {
                    setCurrentIndex(index);
                    setSelectedImage(image?.url || image?.video_url);
                  }}
                >
                  {currentIndex !== index && (
                    <div className="bg-black/60 z-10 w-full h-full absolute top-0 left-0"></div>
                  )}
                  <Image
                    src={thumbnailUrl}
                    alt={`Thumbnail ${index}`}
                    height={1000}
                    width={1000}
                    className="h-full w-full object-fill min-w-[96px]"
                  />
                </Box>
              );
            })}
          </Box>

          {/* Previous Button */}
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: "absolute",
              top: "50%",
              left: 10,
              zIndex: 10,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
              transform: "translateY(-50%)",
            }}
            className="md:-left-10"
          >
            <RightArrowBlackIcon className="rotate-180" />
          </IconButton>

          {/* Next Button */}
          <IconButton
            onClick={handleNext}
            sx={{
              position: "absolute",
              top: "50%",
              right: 10,
              zIndex: 10,
              color: "white",
              backgroundColor: "rgba(0,0,0,0.5)",
              transform: "translateY(-50%)",
            }}
            className="md:-right-10"
          >
            <RightArrowBlackIcon />
          </IconButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ImageModal;
